import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { UseFiltersPayload } from '@hooks/useFilters'
import useModals from '@hooks/useModals'
import useNumericParams from '@hooks/useNumericParams'
import useSetState from '@hooks/useSetState'

import BulkCreateClientUsersModal from '@modals/BulkCreateClientUsersModal'

import { ClientUserModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    deleteClientUser: (payload: HandleCallbackActionParams) => Promise<object>,
  },
}

const defaultState = {
  showBulkCreateClientUsersModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateClientUsersModal: {
      bulkCreateClientUsers: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showBulkCreateClientUsersModal: false }),
    }
  }

  return componentCallbacks[component] || {}
}

const Relations = () => {
  const { userId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateClientUsersModal,
  } = state

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteClientUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showBulkCreateClientUsersModal: (payload: { clientUser: ClientUserModel }) => {
        setModalData('BulkCreateClientUsersModal', payload)
        setState({ showBulkCreateClientUsersModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent userId={userId} />

        <BulkCreateClientUsersModal
          callbacks={callbacks('BulkCreateClientUsersModal', setState)}
          showModal={showBulkCreateClientUsersModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Relations
